import { Injectable, computed, signal } from '@angular/core';

export type VolumeChannel = 'master' | 'music' | 'sfx';

export interface AudioSettings {
  muted: boolean;
  master: number;
  music: number;
  sfx: number;
}

export const DEFAULT_AUDIO_SETTINGS: Readonly<AudioSettings> = {
  muted: false,
  master: 0.8,
  music: 0.5,
  sfx: 0.8,
};

const clamp01 = (v: number): number => Math.min(1, Math.max(0, v));

/**
 * The player's mute and volume choices, persisted to localStorage. Volumes are
 * 0–1; `AudioService` turns them into gain.
 */
@Injectable({
  providedIn: 'root',
})
export class AudioSettingsService {
  private readonly storageKey = 'snk.audio';

  private readonly current = signal<AudioSettings>(this.readStored());

  readonly settings = this.current.asReadonly();
  readonly muted = computed(() => this.current().muted);

  toggleMute(): void {
    this.setMuted(!this.current().muted);
  }

  setMuted(muted: boolean): void {
    this.update({ muted });
  }

  setVolume(channel: VolumeChannel, value: number): void {
    if (!Number.isFinite(value)) return;
    this.update({ [channel]: clamp01(value) });
  }

  private update(patch: Partial<AudioSettings>): void {
    const next = { ...this.current(), ...patch };
    this.current.set(next);
    try {
      localStorage.setItem(this.storageKey, JSON.stringify(next));
    } catch {
      // Storage full or blocked: keep the in-memory value for this session.
    }
  }

  private readStored(): AudioSettings {
    let raw: string | null = null;
    try {
      raw = localStorage.getItem(this.storageKey);
    } catch {
      return { ...DEFAULT_AUDIO_SETTINGS };
    }
    if (!raw) return { ...DEFAULT_AUDIO_SETTINGS };

    try {
      const parsed: unknown = JSON.parse(raw);
      if (typeof parsed !== 'object' || parsed === null) {
        return { ...DEFAULT_AUDIO_SETTINGS };
      }
      const p = parsed as Record<string, unknown>;
      const volume = (key: VolumeChannel): number =>
        typeof p[key] === 'number' && Number.isFinite(p[key])
          ? clamp01(p[key] as number)
          : DEFAULT_AUDIO_SETTINGS[key];
      return {
        muted:
          typeof p['muted'] === 'boolean'
            ? p['muted']
            : DEFAULT_AUDIO_SETTINGS.muted,
        master: volume('master'),
        music: volume('music'),
        sfx: volume('sfx'),
      };
    } catch {
      return { ...DEFAULT_AUDIO_SETTINGS };
    }
  }
}
